import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { type DevicesResponse, ApiError } from "@/lib/api";

interface AuthState {
  status: "loading" | "authenticated" | "unauthenticated";
  email: string | null;
  licenseKey: string | null;
}

interface AuthContextValue extends AuthState {
  signOut: () => void;
  recheckAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: "loading",
    email: null,
    licenseKey: null,
  });

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch("/api/user/devices", {
        credentials: "include",
      });

      if (res.status === 401) {
        setState({
          status: "unauthenticated",
          email: null,
          licenseKey: null,
        });
        return;
      }

      if (!res.ok) {
        setState({
          status: "unauthenticated",
          email: null,
          licenseKey: null,
        });
        return;
      }

      const data = (await res.json()) as DevicesResponse;
      setState({
        status: "authenticated",
        email: data.email,
        licenseKey: data.licenseKey,
      });
    } catch (_err: unknown) {
      if (_err instanceof ApiError && _err.status === 401) {
        setState({
          status: "unauthenticated",
          email: null,
          licenseKey: null,
        });
      } else {
        setState({
          status: "unauthenticated",
          email: null,
          licenseKey: null,
        });
      }
    }
  }, []);

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  const signOut = useCallback(() => {
    document.cookie =
      "blazeboard.session_token=; Path=/; Domain=.blazeboard.co; Max-Age=0; SameSite=Lax";
    document.cookie =
      "blazeboard.session_token=; Path=/; Max-Age=0; SameSite=Lax";
    setState({ status: "unauthenticated", email: null, licenseKey: null });
    window.location.href = "/";
  }, []);

  return (
    <AuthContext.Provider
      value={{ ...state, signOut, recheckAuth: checkAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
